import { useEffect, useState } from 'react';
import Layout from '../components/layout';
import Title from '../components/title';
import { supabase } from '../supabase';
import './tablePage.css';
import searchIcon from '../assets/search.svg';
import editIcon from '../assets/edit.svg';
import deleteIcon from '../assets/delete.svg';

const USERS_PER_PAGE = 4;

const Users = () => {
  const [users, setUsers] = useState([]);
  const [search, setSearch] = useState('');
  const [currentPage, setCurrentPage] = useState(1);

  const [name, setName] = useState('');
  const [email, setEmail] = useState('');
  const [role, setRole] = useState('user');
  const [isCreateOpen, setIsCreateOpen] = useState(false);

  const [id, setId] = useState('');
  const [editName, setEditName] = useState('');
  const [editEmail, setEditEmail] = useState('');
  const [editRole, setEditRole] = useState('user');
  const [isEditOpen, setIsEditOpen] = useState(false);

  const getUsers = async () => {
    const res = await supabase.from('users').select('*').order('id', { ascending: false });
    setUsers(res.data || []);
  };

  useEffect(() => {
    getUsers();
  }, []);

  const createUser = async (e) => {
    e.preventDefault();

    await supabase.from('users').insert([{
      name: name,
      email: email,
      role: role,
    }]);

    setName('');
    setEmail('');
    setRole('user');
    setIsCreateOpen(false);
    await getUsers();
  };

  const editUser = (user) => {
    setId(user.id);
    setEditName(user.name || '');
    setEditEmail(user.email || '');
    setEditRole(user.role || 'user');
    setIsEditOpen(true);
  };

  const updateUser = async (e) => {
    e.preventDefault();

    await supabase
      .from('users')
      .update({
        name: editName,
        email: editEmail,
        role: editRole,
      })
      .eq('id', id);

    setIsEditOpen(false);
    await getUsers();
  };

  const deleteUser = async (userId) => {
    await supabase.from('users').delete().eq('id', userId);
    await getUsers();
  };

  const filteredUsers = users.filter((user) => {
    const value = search.toLowerCase();
    return (
      user.id.toString().includes(value) ||
      user.name?.toLowerCase().includes(value) ||
      user.email?.toLowerCase().includes(value) ||
      user.role?.toLowerCase().includes(value)
    );
  });

  const totalPages = Math.max(1, Math.ceil(filteredUsers.length / USERS_PER_PAGE));
  const startIndex = (currentPage - 1) * USERS_PER_PAGE;
  const paginatedUsers = filteredUsers.slice(startIndex, startIndex + USERS_PER_PAGE);

  return (
    <Layout>
      <div className="table_page">
        <Title subtitle="Manage users" title="Users" />

        <div className="table_controls_card">
          <label className="table_search" htmlFor="users-search">
            <img alt="" src={searchIcon} />
            <input
              id="users-search"
              onChange={(e) => {
                setSearch(e.target.value);
                setCurrentPage(1);
              }}
              placeholder="Search users..."
              type="text"
              value={search}
            />
          </label>

          <div className="table_controls_actions">
            <span>{filteredUsers.length} total records</span>
            <button className="table_primary_button" onClick={() => setIsCreateOpen(true)} type="button">
              Add User
            </button>
          </div>
        </div>

        <div className="table_card">
          <div className="table_scroll">
            <table className="data_table">
              <thead>
                <tr>
                  <th>ID</th>
                  <th>Name</th>
                  <th>Email</th>
                  <th>Role</th>
                  <th>Actions</th>
                </tr>
              </thead>
              <tbody>
                {paginatedUsers.map((user) => (
                  <tr key={user.id}>
                    <td>{user.id}</td>
                    <td>{user.name}</td>
                    <td>{user.email}</td>
                    <td>{user.role}</td>
                    <td>
                      <div className="table_actions">
                        <button
                          aria-label={`Edit user #${user.id}`}
                          className="table_icon_button"
                          onClick={() => editUser(user)}
                          type="button"
                        >
                          <img alt="" src={editIcon} />
                        </button>
                        <button
                          aria-label={`Delete user #${user.id}`}
                          className="table_icon_button table_icon_button_danger"
                          onClick={() => deleteUser(user.id)}
                          type="button"
                        >
                          <img alt="" src={deleteIcon} />
                        </button>
                      </div>
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>

          <div className="table_pagination">
            <button disabled={currentPage === 1} onClick={() => setCurrentPage(currentPage - 1)} type="button">
              Previous
            </button>
            <span>Page {currentPage} of {totalPages}</span>
            <button disabled={currentPage === totalPages} onClick={() => setCurrentPage(currentPage + 1)} type="button">
              Next
            </button>
          </div>
        </div>

        {isCreateOpen && (
          <div
            aria-labelledby="add-user-heading"
            aria-modal="true"
            className="table_modal_overlay"
            onClick={() => setIsCreateOpen(false)}
            role="dialog"
          >
            <div className="table_modal" onClick={(e) => e.stopPropagation()}>
              <div className="table_modal_header">
                <div>
                  <h2 id="add-user-heading">Add User</h2>
                </div>
                <button
                  aria-label="Close add user form"
                  className="table_modal_close"
                  onClick={() => setIsCreateOpen(false)}
                  type="button"
                >
                  &#x2715;
                </button>
              </div>

              <form className="table_form" onSubmit={createUser}>
                <div className="table_form_grid">
                  <label className="table_form_field">
                    <span>Name</span>
                    <input onChange={(e) => setName(e.target.value)} placeholder="Name" value={name} />
                  </label>
                  <label className="table_form_field">
                    <span>Email</span>
                    <input onChange={(e) => setEmail(e.target.value)} placeholder="Email" value={email} />
                  </label>
                  <label className="table_form_field table_form_field_full">
                    <span>Role</span>
                    <select onChange={(e) => setRole(e.target.value)} value={role}>
                      <option value="user">user</option>
                      <option value="admin">admin</option>
                    </select>
                  </label>
                </div>
                <div className="table_form_actions">
                  <button className="table_secondary_button" onClick={() => setIsCreateOpen(false)} type="button">
                    Cancel
                  </button>
                  <button className="table_primary_button" type="submit">Create</button>
                </div>
              </form>
            </div>
          </div>
        )}

        {isEditOpen && (
          <div
            aria-labelledby="edit-user-heading"
            aria-modal="true"
            className="table_modal_overlay"
            onClick={() => setIsEditOpen(false)}
            role="dialog"
          >
            <div className="table_modal" onClick={(e) => e.stopPropagation()}>
              <div className="table_modal_header">
                <div>
                  <h2 id="edit-user-heading">Edit User</h2>
                </div>
                <button
                  aria-label="Close edit user form"
                  className="table_modal_close"
                  onClick={() => setIsEditOpen(false)}
                  type="button"
                >
                  &#x2715;
                </button>
              </div>
              <form className="table_form" onSubmit={updateUser}>
                <div className="table_form_grid">
                  <label className="table_form_field">
                    <span>Name</span>
                    <input onChange={(e) => setEditName(e.target.value)} placeholder="Name" value={editName} />
                  </label>
                  <label className="table_form_field">
                    <span>Email</span>
                    <input onChange={(e) => setEditEmail(e.target.value)} placeholder="Email" value={editEmail} />
                  </label>
                  <label className="table_form_field table_form_field_full">
                    <span>Role</span>
                    <select onChange={(e) => setEditRole(e.target.value)} value={editRole}>
                      <option value="user">user</option>
                      <option value="admin">admin</option>
                    </select>
                  </label>
                </div>
                <div className="table_form_actions">
                  <button className="table_secondary_button" onClick={() => setIsEditOpen(false)} type="button">
                    Cancel
                  </button>
                  <button className="table_primary_button" type="submit">Save</button>
                </div>
              </form>
            </div>
          </div>
        )}
      </div>
    </Layout>
  );
};

export default Users;
